import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Target, SpinnerGap } from '@phosphor-icons/react';

const MONTHS = ['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

export default function ForecastGate({ children }) {
  const { activeId, viewingUser } = useAuth();
  const [checking, setChecking] = useState(true);
  const [hasForecast, setHasForecast] = useState(true);
  const [currentAno, setCurrentAno] = useState(new Date().getFullYear());
  const [currentMes, setCurrentMes] = useState(new Date().getMonth() + 1);
  const [form, setForm] = useState({ volume_pessoal: '', volume_kits: '', volume_clientes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Only gate the logged-in user's own dashboard — not when viewing another person via X-View-As
  const isViewingOther = !!viewingUser;

  const check = async () => {
    if (isViewingOther) { setChecking(false); setHasForecast(true); return; }
    setChecking(true);
    try {
      const { data } = await api.get('/dashboard/currentForecast');
      if (data?.ano) setCurrentAno(data.ano);
      if (data?.mes) setCurrentMes(data.mes);
      setHasForecast(!!data?.data);
    } catch {
      setHasForecast(true); // fail open to avoid locking user out on network errors
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => { check(); /* eslint-disable-next-line */ }, [activeId, isViewingOther]);

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      const { data } = await api.post('/dashboard/forecasts/add', {
        ano: currentAno,
        mes: currentMes,
        volume_pessoal: Number(form.volume_pessoal || 0),
        volume_kits: Number(form.volume_kits || 0),
        volume_clientes: Number(form.volume_clientes || 0),
      });
      if (data?.success) {
        setHasForecast(true);
      } else {
        setError(data?.message || 'Erro ao criar previsão.');
      }
    } catch (e) {
      setError('Erro ao criar previsão.');
    } finally { setSaving(false); }
  };

  if (checking) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#F9FAFB]">
        <SpinnerGap size={28} className="animate-spin text-[#0F1829]" />
      </div>
    );
  }

  if (hasForecast) return children;

  const total = (Number(form.volume_pessoal || 0) + Number(form.volume_kits || 0) + Number(form.volume_clientes || 0));
  const canSubmit = total > 0 && !saving;

  return (
    <div data-testid="forecast-gate" className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="w-full max-w-xl bg-white border border-[#E5E7EB] rounded-2xl shadow-xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-xl bg-[#0F1829]/10">
            <Target size={28} weight="duotone" className="text-[#0F1829]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Definir objetivo do mês</h1>
            <p className="text-sm text-[#6B7280] mt-0.5">Ainda não tem objetivo definido para <span className="font-semibold text-[#111827]">{MONTHS[currentMes]} {currentAno}</span>. Antes de continuar, defina os valores previstos para este mês.</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1.5 uppercase tracking-[0.08em]">Volume Pessoal (€)</label>
            <input autoFocus data-testid="gate-vol-pessoal" type="number" step="0.01" min="0"
              value={form.volume_pessoal} onChange={e => setForm(p => ({ ...p, volume_pessoal: e.target.value }))}
              placeholder="0,00" className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829]/30 focus:border-[#0F1829] outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1.5 uppercase tracking-[0.08em]">Volume Kits (€)</label>
            <input data-testid="gate-vol-kits" type="number" step="0.01" min="0"
              value={form.volume_kits} onChange={e => setForm(p => ({ ...p, volume_kits: e.target.value }))}
              placeholder="0,00" className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829]/30 focus:border-[#0F1829] outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1.5 uppercase tracking-[0.08em]">Volume Clientes (€)</label>
            <input data-testid="gate-vol-clientes" type="number" step="0.01" min="0"
              value={form.volume_clientes} onChange={e => setForm(p => ({ ...p, volume_clientes: e.target.value }))}
              placeholder="0,00" className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829]/30 focus:border-[#0F1829] outline-none" />
          </div>

          <div className="flex items-center justify-between py-3 border-t border-[#E5E7EB]">
            <span className="text-sm font-medium text-[#6B7280]">Total previsto</span>
            <span className="text-lg font-bold text-[#0F1829]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(total)}
            </span>
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{error}</p>}

          <button data-testid="gate-submit" onClick={submit} disabled={!canSubmit}
            className="w-full px-5 py-3 bg-[#0F1829] text-white text-sm font-semibold rounded-md hover:bg-[#001f6e] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            {saving ? 'A guardar...' : 'Definir objetivo e continuar'}
          </button>
          <p className="text-xs text-[#9CA3AF] text-center">O objetivo é obrigatório para acesso à plataforma. Pode ser editado depois em Objetivos → Previsões Pessoais.</p>
        </div>
      </div>
    </div>
  );
}
